import i18n from 'i18next'
import type { BackendModule, ReadCallback } from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import ja from './locales/ja.json'
import en from './locales/en.json'

// #2115 中国語は簡体字 (zh) と繁体字 (zh-TW) の 2 本。台湾・香港のお客様は繁体字を読む。
export const supportedLocales = ['ja', 'en', 'zh', 'zh-TW', 'ko', 'fr', 'de', 'es', 'th', 'vi', 'id'] as const
export type Locale = (typeof supportedLocales)[number]

/**
 * いま客が選んでいる言語。**`zh-TW` を `zh` に潰さない。**
 *
 * i18next の `language` は `zh-TW` や `en-US` のような地域付きで返る。これを
 * `split('-')[0]` で切ると、繁体字を選んだ台湾・香港のお客様に**簡体字が届く**
 * (#2115 で zh-TW を足したとき、ここまで直っていなかった)。
 *
 * 一覧にある言語はそのまま、地域違い (en-US → en) だけ頭を落とす。
 * サーバ側の `utils/locale.py` の `normalize_locale` と同じ規則。
 */
export function normalizeLocale(raw: string | null | undefined): Locale {
  const value = (raw || '').trim()
  const exact = supportedLocales.find((l) => l.toLowerCase() === value.toLowerCase())
  if (exact) return exact
  const head = value.split('-')[0].toLowerCase()
  return supportedLocales.find((l) => l === head) ?? 'ja'
}

/** いま客が選んでいる言語 (i18next の現在値から)。 */
export function currentLocale(): Locale {
  return normalizeLocale(i18n.language)
}

// #1150: ロケール JSON は 10 本合計 ~1.6MB あり、全て静的 import すると main
// bundle の最大級の重りになる。fallback チェーン (en → ja) は t() が同期に
// 引けないと欠損キーがキー名のまま表示されるため bundle に残し、残り 8 本は
// 選択時に dynamic import する (initReactI18next の Suspense が main.tsx の
// root Suspense でロード完了まで描画を止めるので、キー名がちらつくことはない)。
const lazyLocales: Record<string, () => Promise<{ default: Record<string, string> }>> = {
  zh: () => import('./locales/zh.json'),
  'zh-TW': () => import('./locales/zh-TW.json'),
  ko: () => import('./locales/ko.json'),
  fr: () => import('./locales/fr.json'),
  de: () => import('./locales/de.json'),
  es: () => import('./locales/es.json'),
  th: () => import('./locales/th.json'),
  vi: () => import('./locales/vi.json'),
  id: () => import('./locales/id.json'),
}

const lazyLocaleBackend: BackendModule = {
  type: 'backend',
  init: () => {},
  read: (lng: string, _ns: string, callback: ReadCallback) => {
    const load = lazyLocales[lng]
    if (!load) {
      // en / ja は resources で同期供給済み。未知の言語は空で返す
      // (supportedLngs で弾かれるため通常は到達しない)
      callback(null, {})
      return
    }
    load().then(
      (mod) => callback(null, mod.default),
      (err) => callback(err as Error, null),
    )
  },
}

i18n
  .use(lazyLocaleBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // resources に無い言語だけ backend (上の dynamic import) から取得する
    partialBundledLanguages: true,
    resources: {
      ja: { translation: ja },
      en: { translation: en },
    },
    // 店舗オーナー系キーは ja にのみ存在するため、ja を最終フォールバックに含める
    // （詳細は CLAUDE.md の「i18n ルール（店舗オーナー側は ja-only）」参照）
    // #2115 繁体字は、訳が無ければ簡体字 → 英語 → 日本語 の順に落とす
    // (同じ中国語なので、英語へ飛ばすより読める)
    fallbackLng: { 'zh-TW': ['zh', 'en', 'ja'], default: ['en', 'ja'] },
    supportedLngs: supportedLocales as unknown as string[],
    keySeparator: false,
    interpolation: {
      escapeValue: false,
      prefix: '{',
      suffix: '}',
    },
    detection: {
      // #2173 `?lang=en` で言語を決めて開ける (紹介ページの自治体向けの体験で、旅行者のスマホを
      // 英語・中国語などで見せる)。決めた言語は localStorage に残る。名前は `lang` —
      // i18next 既定の `lng` は、この app では経度 (?lat=&lng=, useGeolocation) に使っている
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'yokoso-locale',
      caches: ['localStorage'],
    },
  })

export default i18n
